import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { SkeletonText, SkeletonChart } from './ui/Skeletons';
import { Flame, MousePointerClick, AlertTriangle, ArrowDownToLine, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export default function HeatmapView({ projectId }) {
  const [loadingPages, setLoadingPages] = useState(true);
  const [pages, setPages] = useState([]);
  const [selectedUrl, setSelectedUrl] = useState('');
  
  const [heatmapData, setHeatmapData] = useState(null);
  const [loadingHeatmap, setLoadingHeatmap] = useState(false);
  
  const [activeLayer, setActiveLayer] = useState('all'); // 'all', 'clicks', 'rage', 'dead', 'mouse'

  // Fetch top pages to populate dropdown
  useEffect(() => {
    const fetchPages = async () => {
      try {
        setLoadingPages(true);
        const res = await axios.get(`/analytics/${projectId}/overview?days=30`);
        const topPages = res.data?.top_pages || [];
        const urls = topPages.map(p => p.url).filter(Boolean);
        // Add root / if empty
        if (urls.length === 0) urls.push('/');
        setPages(urls);
        setSelectedUrl(urls[0]);
      } catch (err) {
        console.error("Failed to fetch pages", err);
        setPages(['/']);
        setSelectedUrl('/');
      } finally {
        setLoadingPages(false);
      }
    };
    fetchPages();
  }, [projectId]);

  // Fetch heatmap data when URL changes, and auto-refresh every 5 minutes
  useEffect(() => {
    if (!selectedUrl) return;
    const fetchHeatmap = async () => {
      try {
        setLoadingHeatmap(true);
        const res = await axios.get(`/analytics/${projectId}/heatmap?url=${encodeURIComponent(selectedUrl)}`);
        setHeatmapData(res.data);
      } catch (err) {
        toast.error("Failed to load heatmap data");
        setHeatmapData(null);
      } finally {
        setLoadingHeatmap(false);
      }
    };
    fetchHeatmap();
    // Auto-refresh every 5 minutes (300000ms)
    const interval = setInterval(fetchHeatmap, 300000);
    return () => clearInterval(interval);
  }, [projectId, selectedUrl]);

    if (loadingPages) {
      return (
        <div className="flex flex-col gap-6 p-6 min-h-screen">
          <div className="flex gap-4 mb-4">
            <SkeletonText width="w-[300px]" height="h-12" />
            <SkeletonText width="w-[200px]" height="h-12" />
          </div>
          <SkeletonChart type="circle" height="h-[600px]" />
        </div>
      );
    }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white dark:bg-card p-6 rounded-2xl border border-slate-200 dark:border-border shadow-sm">
        <div>
          <h2 className="text-xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <Flame className="w-6 h-6 text-orange-500" />
            Page Heatmaps
          </h2>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            Replay user behavior with click overlays and scroll depth analysis.
          </p>
        </div>
        
        <div className="w-full sm:w-auto">
          <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1">Select Page</label>
          <select 
            value={selectedUrl}
            onChange={(e) => setSelectedUrl(e.target.value)}
            className="w-full sm:w-80 h-10 px-3 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg text-sm text-slate-800 dark:text-slate-200 outline-none focus:border-[#FEAB3D] focus:ring-1 focus:ring-[#FEAB3D]"
          >
            {pages.map((url, i) => (
              <option key={i} value={url}>{url}</option>
            ))}
          </select>
        </div>
      </div>

      {loadingHeatmap ? (
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 animate-pulse">
          {/* Sidebar Metrics Skeleton */}
          <div className="lg:col-span-1 space-y-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="p-5 border border-slate-200 dark:border-slate-800 bg-white dark:bg-card shadow-sm rounded-xl h-[110px]">
                <div className="flex items-center justify-between mb-3">
                  <div className="h-4 w-24 bg-slate-200 dark:bg-slate-700 rounded"></div>
                  <div className="w-4 h-4 bg-slate-200 dark:bg-slate-700 rounded-full"></div>
                </div>
                <div className="h-8 w-16 bg-slate-200 dark:bg-slate-700 rounded"></div>
              </div>
            ))}
            <div className="p-5 bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-slate-200 dark:border-slate-700 h-[216px]">
               <div className="h-3 w-20 bg-slate-200 dark:bg-slate-700 rounded mb-4"></div>
               <div className="space-y-2">
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="h-9 w-full bg-slate-200 dark:bg-slate-700 rounded-lg"></div>
                  ))}
               </div>
            </div>
          </div>
          {/* Heatmap Visualizer Skeleton */}
          <div className="lg:col-span-3 bg-slate-100 dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 overflow-hidden relative shadow-inner h-[600px] flex flex-col">
            <div className="w-full h-12 bg-slate-200 dark:bg-slate-800 border-b border-slate-300 dark:border-slate-700 flex items-center px-4 gap-2">
               <div className="flex gap-1.5">
                 <div className="w-3 h-3 rounded-full bg-slate-300 dark:bg-slate-600"></div>
                 <div className="w-3 h-3 rounded-full bg-slate-300 dark:bg-slate-600"></div>
                 <div className="w-3 h-3 rounded-full bg-slate-300 dark:bg-slate-600"></div>
               </div>
               <div className="flex-1 mx-4 max-w-xl h-6 bg-slate-300 dark:bg-slate-600 rounded-md"></div>
            </div>
            <div className="flex-1 w-full bg-white dark:bg-[#121212]"></div>
          </div>
        </div>
      ) : heatmapData ? (
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          
          {/* Sidebar Metrics */}
          <div className="lg:col-span-1 space-y-4">
            <Card className="p-5 border-slate-200 dark:border-slate-800 bg-white dark:bg-card shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <span className="text-sm font-semibold text-slate-600 dark:text-slate-400">Avg Scroll Depth</span>
                <ArrowDownToLine className="w-4 h-4 text-[#FEAB3D]" />
              </div>
              <div className="text-3xl font-bold text-slate-900 dark:text-white">
                {Math.round(heatmapData.avg_scroll)}%
              </div>
              <div className="w-full bg-slate-100 dark:bg-slate-800 h-2 mt-4 rounded-full overflow-hidden">
                <div 
                  className="bg-[#FEAB3D] h-full rounded-full transition-all duration-1000"
                  style={{ width: `${Math.round(heatmapData.avg_scroll)}%` }}
                />
              </div>
            </Card>

            <Card className="p-5 border-slate-200 dark:border-slate-800 bg-white dark:bg-card shadow-sm">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-semibold text-slate-600 dark:text-slate-400">Total Clicks</span>
                <MousePointerClick className="w-4 h-4 text-yellow-500" />
              </div>
              <div className="text-3xl font-bold text-slate-900 dark:text-white">
                {heatmapData.clicks.length}
              </div>
            </Card>

            <Card className="p-5 border-slate-200 dark:border-slate-800 bg-white dark:bg-card shadow-sm">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-semibold text-slate-600 dark:text-slate-400">Rage Clicks</span>
                <AlertTriangle className="w-4 h-4 text-red-500" />
              </div>
              <div className="text-3xl font-bold text-slate-900 dark:text-white">
                {heatmapData.rageclicks.length}
              </div>
              <p className="text-xs text-slate-500 mt-2">Signs of user frustration.</p>
            </Card>

            <Card className="p-5 border-slate-200 dark:border-slate-800 bg-white dark:bg-card shadow-sm">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-semibold text-slate-600 dark:text-slate-400">Dead Clicks</span>
                <MousePointerClick className="w-4 h-4 text-orange-500" />
              </div>
              <div className="text-3xl font-bold text-slate-900 dark:text-white">
                {heatmapData.deadclicks.length}
              </div>
              <p className="text-xs text-slate-500 mt-2">Clicks on non-interactive elements.</p>
            </Card>

            <div className="p-5 bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-slate-200 dark:border-slate-700">
              <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-3">Layer Filters</p>
              <div className="space-y-2">
                {['all', 'clicks', 'rage', 'mouse'].map(layer => (
                  <button
                    key={layer}
                    onClick={() => setActiveLayer(layer)}
                    className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-colors ${activeLayer === layer ? 'bg-white dark:bg-slate-700 text-[#021426] dark:text-[#FEAB3D] shadow-sm' : 'text-slate-600 dark:text-slate-400 hover:bg-white/50 dark:hover:bg-slate-800'}`}
                  >
                    {layer.charAt(0).toUpperCase() + layer.slice(1)} {layer !== 'all' && 'Only'}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Heatmap Visualizer Canvas */}
          <div className="lg:col-span-3 bg-slate-100 dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 overflow-hidden relative shadow-inner">
            <div className="w-full h-12 bg-slate-200 dark:bg-slate-800 border-b border-slate-300 dark:border-slate-700 flex items-center px-4 gap-2">
              <div className="flex gap-1.5">
                <div className="w-3 h-3 rounded-full bg-red-400"></div>
                <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
                <div className="w-3 h-3 rounded-full bg-green-400"></div>
              </div>
              <div className="ml-4 flex-1 h-7 bg-white dark:bg-slate-900 rounded flex items-center px-3 text-xs text-slate-500 font-mono truncate shadow-sm">
                {selectedUrl}
              </div>
            </div>
            
            <div className="w-full h-[700px] overflow-y-auto overflow-x-hidden relative custom-scrollbar">
              <div className="absolute top-0 left-0" style={{ width: '100%', height: '3000px', transformOrigin: 'top left' }}>
                {/* Try to load the iframe for visual context. Will show blank if X-Frame-Options blocks it, which is fine, the dots will still show */}
                <iframe 
                  src={selectedUrl.startsWith('http') ? selectedUrl : `https://${selectedUrl}`} 
                  title="Heatmap Overlay"
                  className="w-full h-full opacity-30 pointer-events-none grayscale"
                />
                
                {/* Overlay SVG for data points */}
                <svg className="absolute top-0 left-0 w-full h-full pointer-events-none">
                  <defs>
                    <radialGradient id="clickGrad" cx="50%" cy="50%" r="50%">
                      <stop offset="0%" stopColor="rgba(250, 204, 21, 0.8)" />
                      <stop offset="100%" stopColor="rgba(250, 204, 21, 0)" />
                    </radialGradient>
                    <radialGradient id="rageGrad" cx="50%" cy="50%" r="50%">
                      <stop offset="0%" stopColor="rgba(239, 68, 68, 0.9)" />
                      <stop offset="100%" stopColor="rgba(239, 68, 68, 0)" />
                    </radialGradient>
                    <radialGradient id="deadGrad" cx="50%" cy="50%" r="50%">
                      <stop offset="0%" stopColor="rgba(249, 115, 22, 0.9)" />
                      <stop offset="100%" stopColor="rgba(249, 115, 22, 0)" />
                    </radialGradient>
                  </defs>

                  {/* Mouse move trail — connected lines + dots for high visibility */}
                  {(activeLayer === 'all' || activeLayer === 'mouse') && heatmapData.mousemoves.length > 1 && (
                    <polyline
                      points={heatmapData.mousemoves.map(m => `${m.x},${m.y}`).join(' ')}
                      fill="none"
                      stroke="rgba(56, 189, 248, 0.15)"
                      strokeWidth="2"
                    />
                  )}
                  {(activeLayer === 'all' || activeLayer === 'mouse') && heatmapData.mousemoves.map((m, i) => (
                    <circle key={`m-${i}`} cx={m.x} cy={m.y} r="4" fill="rgba(56, 189, 248, 0.45)" />
                  ))}

                  {/* Standard Clicks */}
                  {(activeLayer === 'all' || activeLayer === 'clicks') && heatmapData.clicks.map((c, i) => (
                    <circle key={`c-${i}`} cx={c.x} cy={c.y} r="20" fill="url(#clickGrad)" opacity="0.7" />
                  ))}

                  {/* Dead Clicks */}
                  {(activeLayer === 'all' || activeLayer === 'clicks') && heatmapData.deadclicks.map((c, i) => (
                    <circle key={`d-${i}`} cx={c.x} cy={c.y} r="22" fill="url(#deadGrad)" opacity="0.8" />
                  ))}

                  {/* Rage Clicks */}
                  {(activeLayer === 'all' || activeLayer === 'rage') && heatmapData.rageclicks.map((c, i) => (
                    <circle key={`r-${i}`} cx={c.x} cy={c.y} r="30" fill="url(#rageGrad)" opacity="0.85" />
                  ))}
                  
                  {/* Scroll depth line */}
                  {heatmapData.avg_scroll > 0 && (
                    <g opacity="0.5">
                      <line x1="0" y1={`${heatmapData.avg_scroll * 30}px`} x2="100%" y2={`${heatmapData.avg_scroll * 30}px`} stroke="#eab308" strokeWidth="2" strokeDasharray="10,10" />
                      <text x="20" y={`${(heatmapData.avg_scroll * 30) - 10}px`} fill="#eab308" fontSize="14" fontWeight="bold">Average Fold ({Math.round(heatmapData.avg_scroll)}%)</text>
                    </g>
                  )}
                </svg>
              </div>
            </div>
            <div className="absolute bottom-4 left-4 bg-white/90 dark:bg-slate-900/90 backdrop-blur border border-slate-200 dark:border-slate-700 px-4 py-3 rounded-xl shadow-lg text-xs space-y-2 pointer-events-none">
               <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-yellow-400"></div> <span className="text-slate-700 dark:text-slate-300">Click</span></div>
               <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-orange-500"></div> <span className="text-slate-700 dark:text-slate-300">Dead Click</span></div>
               <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-red-500"></div> <span className="text-slate-700 dark:text-slate-300">Rage Click</span></div>
               <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-sky-400 opacity-50"></div> <span className="text-slate-700 dark:text-slate-300">Mouse Move</span></div>
            </div>
          </div>
          
        </div>
      ) : null}
    </div>
  );
}
